import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Popconfirm, message, Tag, Modal, Form, Input, DatePicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { makeGetCall, makePostCall, makePutCall, makeDeleteCall, getUserDetails } from '../../utils/helper';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const QuizList = ({ onManageQuestions }) => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const fetchQuizzes = async () => {
    setLoading(true);
    try {
      const response = await makeGetCall('/quiz/list');
      setQuizzes(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch quizzes');
      console.error(error);
    }
    setLoading(false);
  };

  const handleCreateQuiz = () => {
    setEditingQuiz(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditQuiz = (quiz) => {
    setEditingQuiz(quiz);
    form.setFieldsValue({
      name: quiz.quiz_name,
      description: quiz.quiz_description,
      dateRange: [moment(quiz.quiz_start_date), moment(quiz.quiz_end_date)]
    });
    setIsModalVisible(true);
  };

  const handleDeleteQuiz = async (quizId) => {
    try {
      await makeDeleteCall(`/quiz/${quizId}`);
      message.success('Quiz deleted successfully');
      fetchQuizzes();
    } catch (error) {
      message.error('Failed to delete quiz');
      console.error(error);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const userDetails = getUserDetails();

      const payload = {
        name: values.name,
        description: values.description,
        startDate: values.dateRange[0].format('YYYY-MM-DD'),
        endDate: values.dateRange[1].format('YYYY-MM-DD'),
        createdBy: userDetails?.username
      };

      if (editingQuiz) {
        await makePutCall(`/quiz/${editingQuiz.quiz_id}`, payload);
        message.success('Quiz updated successfully');
      } else {
        await makePostCall('/quiz/create', payload);
        message.success('Quiz created successfully');
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchQuizzes();
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      message.error('Failed to save quiz');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Quiz Name',
      dataIndex: 'quiz_name',
      key: 'quiz_name',
      width: '20%',
    },
    {
      title: 'Description',
      dataIndex: 'quiz_description',
      key: 'quiz_description',
      width: '25%',
      ellipsis: true,
    },
    {
      title: 'Questions',
      dataIndex: 'questionCount',
      key: 'questionCount',
      width: '10%',
      align: 'center',
      render: (count) => <Tag color="blue">{count}</Tag>
    },
    {
      title: 'Date Range',
      key: 'dateRange',
      width: '18%',
      render: (_, record) => (
        <div>
          <div>{moment(record.quiz_start_date).format('DD MMM YYYY')}</div>
          <div>{moment(record.quiz_end_date).format('DD MMM YYYY')}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: '10%',
      render: (_, record) => {
        const now = moment();
        const start = moment(record.quiz_start_date);
        const end = moment(record.quiz_end_date);

        if (now.isBefore(start)) {
          return <Tag color="default">Upcoming</Tag>;
        } else if (now.isAfter(end)) {
          return <Tag color="red">Expired</Tag>;
        } else {
          return <Tag color="green">Active</Tag>;
        }
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<QuestionCircleOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onManageQuestions(record);
            }}
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEditQuiz(record);
            }}
          />
          <Popconfirm
            title="Are you sure you want to delete this quiz?"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDeleteQuiz(record.quiz_id);
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div className="mb-4 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Quiz Management</h2>
          <p className="text-gray-500">Create and manage educational quizzes for studies</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateQuiz}>
          Create Quiz
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={quizzes}
        loading={loading}
        rowKey="quiz_id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
          position: ['bottomCenter']
        }}
        scroll={{ x: 1000 }}
        onRow={(record) => ({
          onClick: () => onManageQuestions(record),
          style: { cursor: 'pointer' }
        })}
      />

      <Modal
        title={editingQuiz ? 'Edit Quiz' : 'Create New Quiz'}
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Quiz Name"
            rules={[{ required: true, message: 'Please enter quiz name' }]}
          >
            <Input placeholder="e.g., Prostate MRI Quiz - January 2025" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Brief description of this quiz" />
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Active Period"
            rules={[{ required: true, message: 'Please select date range' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default QuizList;
